import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import { Button } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { API_BASE } from "../api";

function AIContent() {
  useEffect(() => {
    document.title = "AI Content";
  }, []);

  const webcamRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [status, setStatus] = useState("");
  const [timer, setTimer] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  const [timeoutId, setTimeoutId] = useState(null);
  const [maxTimeoutId, setMaxTimeoutId] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);

  // New states for video and finished message
  const [showVideo, setShowVideo] = useState(false);
  const [finishedMessageShown, setFinishedMessageShown] = useState(false);

  // Participant ID input state (used for uploads)
  const [deelnemerId, setDeelnemerId] = useState("");
  // validate deelnemer id: only digits, max 3 chars
  const isValidDeelnemerId = /^\d{3}$/.test(deelnemerId);
  const handleDeelnemerChange = (e) => {
    const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, 3);
    setDeelnemerId(digits);
  };

  // Participant lookup state: null = not checked / input invalid, true = found, false = not found
  const [participantName, setParticipantName] = useState(null);
  const [participantFound, setParticipantFound] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const lookup = async (id) => {
      try {
        const res = await fetch(`${API_BASE}/deelnemers`);
        if (!res.ok) throw new Error("Network response was not ok");
        const data = await res.json();
        if (cancelled) return;
        // match either numeric id or zero-padded 3-digit string
        const found = data.find((p) => String(p.id) === id || String(p.id).padStart(3, "0") === id);
        if (found) {
          setParticipantName(found.naam || found.name || `#${found.id}`);
          setParticipantFound(true);
        } else {
          setParticipantName(null);
          setParticipantFound(false);
        }
      } catch (err) {
        if (!cancelled) {
          setParticipantName(null);
          setParticipantFound(false);
        }
      }
    };

    if (isValidDeelnemerId) {
      setParticipantFound(null); // pending
      lookup(deelnemerId);
    } else {
      setParticipantName(null);
      setParticipantFound(null);
    }

    return () => {
      cancelled = true;
    };
  }, [deelnemerId, isValidDeelnemerId]);

  const uploadPhoto = async (imageSrc, capture_time) => {
    try {
      const blob = await fetch(imageSrc).then((res) => res.blob());
      const formData = new FormData();
      const filename = `${uuidv4()}.jpg`;
      formData.append("photo", blob, filename);
      formData.append("deelnemer_id", deelnemerId);
      formData.append("capture_time", capture_time);

      const response = await fetch(`${API_BASE}/photos`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        setStatus("Failed to upload photo");
      }
    } catch (err) {
      setStatus("Error uploading photo");
    }
  };

  const takePhotoAndUpload = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        const now = new Date();
        const capture_time = now.toISOString().slice(0, 19).replace("T", " ");
        uploadPhoto(imageSrc, capture_time);
      }
    }
  };

  const clearAllTimers = () => {
    // Wis alle mogelijke timers/intervals
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    if (maxTimeoutId) {
      clearTimeout(maxTimeoutId);
      setMaxTimeoutId(null);
    }
  };

  const startCapturing = () => {
    if (!deelnemerId) return;

    // Clear ALL possible running timers/intervals before starting a new session
    clearAllTimers();

    setStatus("");
    setCapturing(true);
    setTimer(0);

    // Start video display
    setShowVideo(true);
    setFinishedMessageShown(false);

    // Take first photo immediately
    takePhotoAndUpload();

    // Every 5 seconds, take a photo
    const id = setInterval(() => {
      setTimer((t) => t + 5);
      takePhotoAndUpload();
    }, 5000);
    setIntervalId(id);

    // After 50 seconds, force end of the questionnaire (same as video end)
    const maxId = setTimeout(() => {
      handleVideoEnded();
    }, 50000);
    setMaxTimeoutId(maxId);
  };

  // Handler when the video finishes playing
  const handleVideoEnded = () => {
    // clear any max-duration timeout
    if (maxTimeoutId) {
      clearTimeout(maxTimeoutId);
      setMaxTimeoutId(null);
    }
    // stop photo capture interval
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    setCapturing(false);
    setShowVideo(false);
    setFinishedMessageShown(true);
    setStatus("Finished capturing photos.");

    // After 5 seconds automatically hide the finished message and return to the input view.
    // Keep deelnemerId intact per previous behavior.
    const id = setTimeout(() => {
      setFinishedMessageShown(false);
      setStatus("");
      setTimer(0);
      // Clear participant id when returning to the input screen
      setDeelnemerId("");
    }, 5000);
    setTimeoutId(id);
  };

  // Click anywhere after finished message to reset UI (controls reappear)
  useEffect(() => {
    const handleClick = () => {
      if (finishedMessageShown) {
        // clear any lingering max-duration timeout just in case
        if (maxTimeoutId) {
          clearTimeout(maxTimeoutId);
          setMaxTimeoutId(null);
        }
        // Also clear photo capture interval if still running
        if (intervalId) {
          clearInterval(intervalId);
          setIntervalId(null);
        }
        setCapturing(false);
        setFinishedMessageShown(false);
        setStatus("");
        setTimer(0);
        // Clear participant id when returning to the input screen
        setDeelnemerId("");
      }
    };
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [finishedMessageShown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
      if (maxTimeoutId) clearTimeout(maxTimeoutId);
    };
    // eslint-disable-next-line
  }, []);

  // Toggle webcam preview with "W" key (keeps existing capability)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "w" || e.key === "W") {
        setShowWebcam((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: "1rem" }}>
      <h1 style={{ fontSize: "3em", color: "white", textAlign: "center", margin: 0 }}>AI Content</h1>
      {/* Show controls only when not capturing and not showing finished message */}
      {!capturing && !finishedMessageShown && (
        <>
          <input
            id="deelnemerId"
            type="text"
            value={deelnemerId}
            onChange={handleDeelnemerChange}
            placeholder="000"
            maxLength={3}
            inputMode="numeric"
            pattern="\\d{3}"
            style={{ width: "30%", fontSize: "5em", textAlign: "center", padding: "0.2em", borderRadius: "0.6em" }}
          />
          {/* Participant name or not-found message */}
          <div style={{ fontSize: "1.5em", marginTop: "0.2em" }}>
            {participantFound === null ? "" : participantFound === true ? participantName : "Participant not found"}
          </div>
          <Button
            variant="primary"
            className="start-button"
            onClick={startCapturing}
            disabled={!participantFound}
            style={{ width: "30%", fontSize: "5em", marginTop: "0.5em", borderRadius: "0.6em" }}
          >
            START
          </Button>
        </>
      )}

      {/* Show video while capturing */}
      {showVideo && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100vh" }}>
          <video
            className="fullscreen-video"
            src="/ai-content.mov"
            autoPlay
            playsInline
            onEnded={handleVideoEnded}
          />
        </div>
      )}

      {/* Finished message */}
      {finishedMessageShown && (
        <div style={{ textAlign: "center", fontSize: "2em", padding: "1em" }}>
          This part of the test is finished. When the whistle blows, you may proceed to the next section.
        </div>
      )}

      {/* Hidden but active webcam for functionality when capturing */}
      <div style={{ position: "absolute", display: "block", zIndex: -100, top: 0, width: 1280, height: 960, overflow: "hidden" }}>
        {capturing && (
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            width={1280}
            height={960}
            style={
              showWebcam
                ? { visibility: "visibile" }
                : { visibility: "hidden" }
            }
          />
        )}
      </div>
    </div>
  );
}

export default AIContent;
